<template>
	<div
		ref="rootEl"
		class="live-clipper grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start"
		tabindex="-1"
		@keydown="onRootKeydown"
		@keyup="onRootKeyup"
	>
		<div class="flex min-w-0 flex-col gap-3">
			<ClipperPlayer
				ref="playerRef"
				:base-url="playerBaseUrl"
				:mode="playerMode"
				:from="playerFrom"
				:duration="playerDuration"
				:loop="preview"
				:ranges="dvr.ranges.value"
				:live-available="dvr.isLive.value"
				:live-edge="dvr.liveEdge.value"
				:stream-name="streamName"
				@time="onTime"
				@live="onPlayerLive"
				@source-change="onSourceChange"
				@playing="onPlaying"
				@error="onPlayerError"
				@segments="onSegments"
			>
				<template #empty>
					<div class="flex w-full flex-col items-center gap-3 px-6 text-center">
						<template v-if="loadingDvr">
							<!-- Skeleton while recording_status / ranges load -->
							<div class="clipper-skeleton flex w-full max-w-md flex-col items-center gap-3" aria-hidden="true">
								<span class="h-14 w-14 rounded-full bg-surface-3" />
								<span class="h-2.5 w-40 rounded bg-surface-3" />
								<span class="h-2 w-64 rounded bg-surface-3" />
							</div>
							<span class="sr-only">Loading recording information</span>
						</template>
						<template v-else-if="dvr.status.value === 'error'">
							<span class="text-sm text-surface-text-2">We could not reach the recording service.</span>
							<span class="text-surface-8">Retrying automatically. You can also try again now.</span>
							<FormButton type="secondary" size="sm" @click="dvr.refresh()">Try again</FormButton>
						</template>
						<template v-else-if="streamLive">
							<span class="inline-flex items-center gap-2 text-sm text-surface-text-2">
								<span class="h-2 w-2 rounded-full bg-error" aria-hidden="true" />
								Live, recording not available yet
							</span>
							<span class="max-w-sm text-surface-8">The stream is publishing. Clipping opens as soon as the first segment is recorded, usually within a few seconds.</span>
						</template>
						<template v-else>
							<span class="text-sm text-surface-text-2">No recordings yet</span>
							<span class="max-w-sm text-surface-8">Clipping becomes available once the stream goes live. Keep this page open and it will start automatically.</span>
							<span v-if="dvr.status.value === 'unknown'" class="max-w-sm text-xs text-surface-8">We have not received this stream yet. Make sure it is publishing and that recording is enabled.</span>
						</template>
					</div>
				</template>
			</ClipperPlayer>

			<!-- Status row: fixed height with reserved slots so live <-> replay never reflows -->
			<div class="grid h-8 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 text-xs text-surface-8">
				<span class="inline-flex min-w-[64px] items-center gap-1.5 font-medium text-surface-text-2">
					<span class="h-2 w-2 shrink-0 rounded-full" :class="stateDot" aria-hidden="true" />
					<span class="truncate">{{ stateLabel }}</span>
				</span>
				<span class="truncate tabular-nums">{{ stateDetail }}</span>
				<div class="flex h-8 items-center justify-end gap-2">
					<span class="hidden truncate tabular-nums md:inline">{{ dvrAvailabilityLabel }}</span>
					<!-- Occupies its slot even when live (invisible + disabled) -->
					<FormButton
						type="link"
						size="xs"
						class="tabular-nums"
						:class="{ invisible: playingLive || !dvr.isLive.value }"
						:disabled="playingLive || !dvr.isLive.value"
						:tabindex="playingLive || !dvr.isLive.value ? -1 : 0"
						title="Jump to live (End)"
						@click="goLive"
					>
						<span class="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-error" />
						Jump to live
					</FormButton>
				</div>
			</div>

			<!-- One-time hint -->
			<div
				v-if="showHint"
				class="flex items-center justify-between gap-3 rounded-md bg-surface-3/60 px-3 py-1.5 text-xs text-surface-8"
			>
				<span class="truncate tabular-nums">
					<kbd class="clipper-kbd">I</kbd> / <kbd class="clipper-kbd">O</kbd> set range · <kbd class="clipper-kbd">Space</kbd> play · <kbd class="clipper-kbd">?</kbd> all shortcuts
				</span>
				<button type="button" class="shrink-0 rounded px-1.5 py-0.5 text-surface-8 hover:text-surface-text-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-main" aria-label="Dismiss hint" @click="dismissHint">
					Got it
				</button>
			</div>

			<!-- Screen reader announcements for committed range changes -->
			<span class="sr-only" role="status" aria-live="polite">{{ announcement }}</span>

			<ClipTimeline
				ref="timelineRef"
				v-model:view="view"
				v-model:selection="selection"
				:ranges="dvr.ranges.value"
				:playhead="playhead"
				:live-edge="dvr.liveEdge.value"
				:max-duration="maxDuration"
				:dvr-extent="dvr.dvrExtent.value"
				:base-url="baseUrl"
				:follow="!preview"
				:export-window="exportWindow"
				:loading="loadingDvr"
				@seek="onSeek"
				@toggle-play="togglePlay"
				@download="downloadFromShortcut"
				@go-live="goLive"
				@interaction="onInteraction"
			>
				<template #empty>
					<template v-if="loadingDvr">Loading recordings</template>
					<template v-else-if="dvr.status.value === 'error'">Recording service unavailable, retrying</template>
					<template v-else-if="streamLive">Live, recording not available yet</template>
					<template v-else>No recordings yet</template>
				</template>
			</ClipTimeline>

			<ClipMinimap
				v-model:view="view"
				class="mb-1"
				:ranges="dvr.ranges.value"
				:dvr-extent="dvr.dvrExtent.value"
				:live-edge="dvr.liveEdge.value"
				:playhead="playhead"
				:selection="selection"
				:selection-valid="selectionValid"
			/>
		</div>

		<ClipPanel
			ref="panelRef"
			v-model:selection="selection"
			v-model:preview="preview"
			:ranges="dvr.ranges.value"
			:live-edge="dvr.liveEdge.value"
			:playing-live="playingLive"
			:playhead="playhead"
			:max-duration="maxDuration"
			:has-dvr="dvr.hasDvr.value"
			:base-url="baseUrl"
			:highlight-id="highlightId"
			:stream-id="streamId"
			:can-screenshot="canScreenshot"
			:dvr-extent="dvr.dvrExtent.value"
			:committed-selection="committedSelection"
			:export-window="exportWindow"
			:bitrate-kbps="dvr.bitrateKbps.value"
			@screenshot="screenshot"
			@notify="onNotify"
			@seek="onSeek"
			@interaction="onInteraction"
		/>
	</div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useNotification } from '@kyvg/vue3-notification';
import FormButton from '@/components/Atoms/FormButton.vue';
import ClipperPlayer from './ClipperPlayer.vue';
import ClipTimeline from './ClipTimeline.vue';
import ClipMinimap from './ClipMinimap.vue';
import ClipPanel from './ClipPanel.vue';
import { useDvrRanges } from './useDvrRanges';
import {
	formatLocalTime,
	formatLocalDateTime,
	clamp,
	rangeAt,
	selectionIsContiguous,
	snapExportWindow,
} from './dvrUtils';
import { track, EVENTS } from '@/lib/tracking';

const props = defineProps({
	// https://castr-highlights.castr.com/highlights/{userId}/{streamKey}
	baseUrl: { type: String, required: true },
	// Plan limit in seconds.
	maxDuration: { type: Number, default: 900 },
	streamName: { type: String, default: '' },
	highlightId: { type: String, default: '' },
	streamId: { type: String, default: '' },
});

const emit = defineEmits(['status']);

const { notify } = useNotification();

const rootEl = ref(null);
const playerRef = ref(null);
const timelineRef = ref(null);
const panelRef = ref(null);

const dvr = useDvrRanges(computed(() => props.baseUrl));

const playerMode = ref('live');
const playerFrom = ref(null);
const playerDuration = ref(null);
const playingLive = ref(true);
const isPlaying = ref(false);
const playhead = ref(null);
const selection = ref(null);
const preview = ref(false);
const view = ref({ from: Date.now() / 1000 - 1800, duration: 1800 });
// Wall-clock segment starts learned from loaded playlists (sorted, capped).
const segmentStarts = ref([]);

// Header status chip: live when the recording is growing or the origin
// currently sees the stream publishing (media_info answers).
const streamLive = computed(() => dvr.isLive.value || dvr.publishing.value === true);
watch(
	() => [dvr.status.value, streamLive.value],
	() => {
		const known = dvr.status.value !== 'idle' && dvr.status.value !== 'loading';
		emit('status', { known, live: streamLive.value, hasDvr: dvr.hasDvr.value, status: dvr.status.value });
	},
	{ immediate: true },
);

let viewInitialized = false;
let playerInitialized = false;
let previewTimer = null;

// Do not hand the player a URL until we know there is something to play.
const playerBaseUrl = computed(() => (dvr.hasDvr.value ? props.baseUrl : ''));
const canScreenshot = computed(() => dvr.hasDvr.value && typeof playhead.value === 'number');

const selectionValid = computed(() => !selection.value
	|| (selectionIsContiguous(dvr.ranges.value, selection.value) && selection.value.end - selection.value.start <= props.maxDuration + 0.001));

// The selection as of the last committed change. While a handle is dragged
// or a nudge button is held this lags behind `selection`, so thumbnails and
// the Selected / Exported readout update on release rather than every tick.
const committedSelection = ref(null);
const interacting = ref(false);
function commitSelection() {
	const sel = selection.value;
	const cur = committedSelection.value;
	if (!sel) {
		if (cur) committedSelection.value = null;
		return;
	}
	if (!cur || cur.start !== sel.start || cur.end !== sel.end) {
		committedSelection.value = { start: sel.start, end: sel.end, source: sel.source || null };
	}
}
watch(selection, () => {
	if (!interacting.value) commitSelection();
}, { deep: true });
function onInteraction(active) {
	interacting.value = !!active;
	if (!active) commitSelection();
}

// What the origin will actually deliver: start snapped down and end snapped
// up to segment boundaries learned from loaded playlists (6s grid otherwise).
const exportWindow = computed(() => {
	const sel = committedSelection.value;
	if (!sel || !selectionIsContiguous(dvr.ranges.value, sel)) return null;
	return snapExportWindow(sel, rangeAt(dvr.ranges.value, sel.start), segmentStarts.value);
});

const playheadLabel = computed(() => (typeof playhead.value === 'number' ? formatLocalTime(playhead.value) : ''));
const recordingSinceLabel = computed(() => {
	const r = dvr.ranges.value;
	if (!r.length) return '';
	return formatLocalTime(r[r.length - 1].start);
});
const lastRecordingLabel = computed(() => {
	const r = dvr.ranges.value;
	if (!r.length) return '';
	const last = r[r.length - 1];
	return `${formatLocalDateTime(last.start)} to ${formatLocalTime(last.end)}`;
});

// ---- status row -----------------------------------------------------------

const loadingDvr = computed(() => dvr.status.value === 'idle' || dvr.status.value === 'loading');

const stateLabel = computed(() => {
	if (loadingDvr.value) return 'Loading';
	if (dvr.status.value === 'error') return 'Unavailable';
	if (streamLive.value) return 'Live';
	return 'Offline';
});

// Paired with text so state never relies on colour alone.
const stateDot = computed(() => {
	if (loadingDvr.value) return 'bg-surface-6 animate-pulse';
	if (dvr.status.value === 'error') return 'bg-warning';
	if (streamLive.value) return 'bg-error';
	return 'bg-surface-6';
});

const stateDetail = computed(() => {
	if (loadingDvr.value) return 'Checking the recording service';
	if (dvr.status.value === 'error') return 'Could not reach the recording service. Retrying.';
	if (!dvr.hasDvr.value) {
		return streamLive.value ? 'Live, recording not available yet' : 'No recordings yet';
	}
	if (dvr.isLive.value) {
		return playingLive.value ? `Recording since ${recordingSinceLabel.value}` : `Replaying ${playheadLabel.value}`;
	}
	const ext = dvr.dvrExtent.value;
	const until = ext ? formatLocalDateTime(ext.end) : lastRecordingLabel.value;
	return `Source offline, recordings available until ${until}`;
});

// "DVR available 19:10–23:10 · rolling window" from recording_status.
const dvrAvailabilityLabel = computed(() => {
	const ext = dvr.dvrExtent.value;
	if (!ext) return '';
	const sameDay = new Date(ext.start * 1000).toDateString() === new Date(ext.end * 1000).toDateString();
	const from = sameDay ? formatLocalTime(ext.start) : formatLocalDateTime(ext.start);
	return `DVR available ${from}–${formatLocalTime(ext.end)} · rolling window`;
});

// ---- hint + announcements -------------------------------------------------

// Shown once per mount, in memory only (no persistence anywhere in the clipper).
const showHint = ref(true);
function dismissHint() {
	showHint.value = false;
}

const announcement = ref('');
watch(committedSelection, (sel) => {
	if (!sel) {
		announcement.value = 'Clip range cleared';
		return;
	}
	const dur = Math.round(sel.end - sel.start);
	announcement.value = `In point ${formatLocalTime(sel.start)}, out point ${formatLocalTime(sel.end)}, duration ${dur} seconds`;
});

// Keep a freshly applied quick clip in view (pan, keep zoom where possible).
watch(committedSelection, (sel) => {
	if (sel && sel.source && sel.source.startsWith('quick')) timelineRef.value?.ensureVisible(sel);
});

// Initial view + player source once the first ranges arrive.
watch(() => [dvr.hasDvr.value, dvr.status.value], () => {
	if (!dvr.hasDvr.value) return;
	const ranges = dvr.ranges.value;
	const edge = dvr.liveEdge.value;
	if (!viewInitialized) {
		viewInitialized = true;
		// Last 10 minutes, live edge (or the end of the last recording) near
		// the right so there is room for it to keep growing.
		const dur = 600;
		view.value = { from: edge - dur * 0.85, duration: dur };
	}
	if (!playerInitialized) {
		playerInitialized = true;
		if (dvr.isLive.value) {
			playerMode.value = 'live';
			playerFrom.value = null;
			playingLive.value = true;
		} else {
			// Offline with DVR: start near the end of the last recording.
			const last = ranges[ranges.length - 1];
			playerMode.value = 'window';
			playerFrom.value = clamp(last.end - 30, last.start, last.end - 1);
			playingLive.value = false;
		}
	}
}, { immediate: true });

// Reset when the stream changes.
watch(() => props.baseUrl, () => {
	viewInitialized = false;
	playerInitialized = false;
	selection.value = null;
	preview.value = false;
	playhead.value = null;
	segmentStarts.value = [];
});

// Keep the composable fetching the ranges the user is looking at.
watch(view, (v) => {
	dvr.setViewport(v.from - v.duration * 0.5, v.from + v.duration * 1.5);
}, { deep: true });

function onTime(t) {
	playhead.value = t;
}

function onPlayerLive(isLive) {
	playingLive.value = isLive;
	if (isLive) {
		playerMode.value = 'live';
		playerFrom.value = null;
		playerDuration.value = null;
		if (preview.value) preview.value = false;
	}
}

function onSourceChange({ mode, from }) {
	playerMode.value = mode;
	playerFrom.value = mode === 'live' ? null : from;
	playingLive.value = mode === 'live';
}

function onPlaying(v) {
	isPlaying.value = v;
}

function onSegments(starts) {
	const merged = new Set(segmentStarts.value);
	for (const s of starts) merged.add(Math.round(s * 1000) / 1000);
	let list = [...merged].sort((a, b) => a - b);
	if (list.length > 4000) list = list.slice(list.length - 4000);
	segmentStarts.value = list;
}

function onPlayerError({ message }) {
	if (message) notify({ group: 'error', title: 'Playback problem', text: message, duration: 4000 });
}

function onSeek(t) {
	if (preview.value) preview.value = false;
	playerDuration.value = null;
	playerMode.value = 'window';
	playerFrom.value = t;
	playingLive.value = false;
	playhead.value = t;
	playerRef.value?.seekWall(t);
}

function goLive() {
	if (preview.value) preview.value = false;
	playerDuration.value = null;
	playerRef.value?.goLive();
}

function togglePlay() {
	playerRef.value?.togglePlay();
}

function downloadFromShortcut() {
	panelRef.value?.download('manual');
}

async function screenshot() {
	const result = await playerRef.value?.captureFrame();
	if (!result) return;
	if (result.ok) {
		track(EVENTS.HIGHLIGHT_SCREENSHOT, { highlightId: props.highlightId, streamId: props.streamId });
		notify({ group: 'success', title: 'Screenshot saved', duration: 2500 });
	} else {
		notify({ group: 'error', title: 'Screenshot failed', text: result.error, duration: 4000 });
	}
}

function onNotify({ group, title, text }) {
	notify({ group, title, text, duration: 3500 });
}

// Preview: play the selection as a looping window playlist.
function applyPreview() {
	if (!preview.value || !selection.value) return;
	playerMode.value = 'window';
	playerFrom.value = selection.value.start;
	playerDuration.value = selection.value.end - selection.value.start;
	playingLive.value = false;
}

watch(preview, (on) => {
	clearTimeout(previewTimer);
	if (on) {
		applyPreview();
	} else {
		playerDuration.value = null;
		if (playerMode.value === 'window' && typeof playhead.value === 'number') {
			playerFrom.value = playhead.value;
		}
	}
});

watch(selection, () => {
	if (!preview.value) return;
	clearTimeout(previewTimer);
	previewTimer = setTimeout(applyPreview, 350);
}, { deep: true });

function onRootKeydown(e) {
	const target = e.target;
	if (!target) return;
	const tag = target.tagName;
	if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) return;
	if (target.closest?.('.clip-timeline__stage')) return; // timeline handles its own keys
	if (tag === 'BUTTON' && (e.key === ' ' || (e.key === 'Enter' && !(e.metaKey || e.ctrlKey)))) return; // let buttons activate
	timelineRef.value?.handleKey(e);
}

function onRootKeyup(e) {
	// Held [ / ] keys started from anywhere in the clipper stop here.
	if (e.target?.closest?.('.clip-timeline__stage')) return;
	timelineRef.value?.handleKeyUp(e);
}

onBeforeUnmount(() => {
	clearTimeout(previewTimer);
});

function setSelection(sel) {
	if (preview.value) preview.value = false;
	selection.value = sel ? { start: sel.start, end: sel.end, source: sel.source || 'manual' } : null;
	if (sel) timelineRef.value?.ensureVisible(sel.start, sel.end);
}

function setView(v) {
	if (v && typeof v.from === 'number' && v.duration > 0) view.value = { from: v.from, duration: v.duration };
}

defineExpose({ goLive, togglePlay, screenshot, setSelection, setView });
</script>

<style scoped>
/* Visible focus for every control inside the clipper (Atoms only tint the ring). */
.live-clipper :deep(button:focus-visible),
.live-clipper :deep(input:focus-visible),
.live-clipper :deep([tabindex]:focus-visible) {
	outline: 2px solid rgb(32, 118, 255);
	outline-offset: 2px;
}

.clipper-kbd {
	display: inline-block;
	min-width: 18px;
	padding: 0 5px;
	border-radius: 4px;
	background: rgba(255, 255, 255, 0.08);
	box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.12);
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
	font-size: 10px;
	line-height: 16px;
	text-align: center;
	color: rgb(209, 219, 240);
}

.clipper-skeleton > * {
	animation: clipper-shimmer 1.4s ease-in-out infinite;
}

@keyframes clipper-shimmer {
	0%,
	100% {
		opacity: 0.45;
	}
	50% {
		opacity: 1;
	}
}

.clipper-dot-pulse {
	width: 10px;
	height: 10px;
	border-radius: 9999px;
	background: rgb(174, 186, 214);
	animation: clipper-pulse 1.2s ease-in-out infinite;
}

@keyframes clipper-pulse {
	0%,
	100% {
		opacity: 0.35;
		transform: scale(0.85);
	}
	50% {
		opacity: 1;
		transform: scale(1);
	}
}
</style>
